import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { extractApiError } from '../../../utils';
import { useFeatureFlag, FEATURE_FLAG_NAMES } from '../../../hooks/useFeatureFlag';
import { getWorkflowDropzoneAccept } from '../../../consts/fileTypes';
import {
  buildConnectUiUrl,
  useCreateConnectSession,
  useImportCloudFiles,
  useIntegrationConnections,
  useIntegrationFiles
} from '../../../services/integrations.service';
import {
  getConnectedProviderKeys,
  getPreviewIntegrationCards,
  getProviderConnections
} from '../../../consts/integrationsCatalog';

const CLOUD_FOLDER_PROVIDERS = ['google-drive', 'sharepoint-online'];

export default function useCloudImportFlow({
  onClose,
  onImported,
  periodId,
  reportType,
  onUploadFromDevice
}) {
  const navigate = useNavigate();
  const createConnectSession = useCreateConnectSession();
  const importCloudFiles = useImportCloudFiles();
  const providers = useMemo(() => getPreviewIntegrationCards(), []);
  const acceptedMimeTypes = useMemo(
    () => new Set(Object.keys(getWorkflowDropzoneAccept(reportType) || {})),
    [reportType]
  );
  const isCloudIntegrationEnabled = useFeatureFlag(
    FEATURE_FLAG_NAMES.FILE_UPLOAD_CLOUD_INTEGRATION
  );

  const [isCloudModalOpen, setIsCloudModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [selectedCloudFiles, setSelectedCloudFiles] = useState([]);
  const [cloudQuery, setCloudQuery] = useState('');
  const [cloudPath, setCloudPath] = useState('');
  const [cloudFolderId, setCloudFolderId] = useState('');
  const [, setCloudFolderIdTrail] = useState([]);

  const {
    data: connectionsData,
    refetch: refetchConnections,
    isFetching: isFetchingConnections
  } = useIntegrationConnections({
    queryOptions: {
      enabled: isCloudModalOpen
    }
  });

  const allConnections = useMemo(() => connectionsData?.connections || [], [connectionsData]);
  const selectedProviderConnections = useMemo(
    () => getProviderConnections(allConnections, selectedProvider),
    [allConnections, selectedProvider]
  );
  const connectedProviders = useMemo(
    () =>
      providers.filter(provider => getConnectedProviderKeys(allConnections).includes(provider.key)),
    [allConnections, providers]
  );

  const {
    data: cloudFilesData,
    refetch: refetchCloudFiles,
    isFetching: isFetchingCloudFiles
  } = useIntegrationFiles({
    params: CLOUD_FOLDER_PROVIDERS.includes(selectedProvider)
      ? {
          provider_key: selectedProvider,
          connection_id: selectedConnectionId,
          query: cloudQuery || undefined,
          folder_id: cloudFolderId || undefined
        }
      : {
          provider_key: selectedProvider,
          connection_id: selectedConnectionId,
          query: cloudQuery || undefined,
          path: cloudPath || undefined
        },
    queryOptions: {
      enabled: !!selectedProvider && !!selectedConnectionId && isCloudModalOpen
    }
  });

  const visibleCloudFiles = useMemo(() => {
    const items = cloudFilesData?.items || [];
    const normalizedQuery = cloudQuery.trim().toLowerCase();

    if (!normalizedQuery) return items;

    return items.filter(file => file?.name?.toLowerCase().includes(normalizedQuery));
  }, [cloudFilesData?.items, cloudQuery]);

  const resetCloudBrowserState = () => {
    setSelectedCloudFiles([]);
    setCloudQuery('');
    setCloudPath('');
    setCloudFolderId('');
    setCloudFolderIdTrail([]);
  };

  const closeCloudModal = () => {
    setIsCloudModalOpen(false);
    resetCloudBrowserState();
  };

  const openCloudModal = () => {
    onClose?.();
    setIsCloudModalOpen(true);
    resetCloudBrowserState();
  };

  const handleSelectProvider = providerKey => {
    setSelectedProvider(providerKey);
    setSelectedConnectionId('');
    resetCloudBrowserState();
  };

  const handleSelectConnection = connectionId => {
    setSelectedConnectionId(connectionId);
    resetCloudBrowserState();
  };

  const toggleCloudFile = file => {
    setSelectedCloudFiles(prev =>
      prev.some(item => item.id === file.id)
        ? prev.filter(item => item.id !== file.id)
        : [...prev, file]
    );
  };

  const getParentCloudPath = path =>
    path.replace(/\\/g, '/').replace(/\/+/g, '/').split('/').filter(Boolean).slice(0, -1).join('/');

  const handleCloudItemClick = file => {
    if (!file?.is_folder) {
      toggleCloudFile(file);
      return;
    }

    if (CLOUD_FOLDER_PROVIDERS.includes(selectedProvider)) {
      setCloudFolderId(file.id);
      setCloudFolderIdTrail(prev => [...prev, file.id].filter(Boolean));
      setCloudPath(prev =>
        [...prev.split('/').filter(Boolean), file.name].filter(Boolean).join('/')
      );
    } else {
      setCloudPath(prev => `${prev || ''}/${file?.name || ''}`.replace(/\/+/g, '/'));
    }

    setSelectedCloudFiles([]);
    setCloudQuery('');
  };

  const handleCloudBack = () => {
    if (CLOUD_FOLDER_PROVIDERS.includes(selectedProvider)) {
      setCloudFolderIdTrail(prev => {
        const nextTrail = prev.slice(0, -1);
        setCloudFolderId(nextTrail[nextTrail.length - 1] || '');
        return nextTrail;
      });
    }

    setCloudPath(prev => getParentCloudPath(prev));
    setSelectedCloudFiles([]);
    setCloudQuery('');
  };

  const handleConnectProvider = async providerKey => {
    try {
      const response = await createConnectSession.mutateAsync({
        allowed_integrations: [providerKey]
      });
      const connectLink = buildConnectUiUrl(response);

      if (connectLink) {
        window.open(connectLink, '_blank', 'noopener,noreferrer');
      }

      setSelectedProvider('');
      setSelectedConnectionId('');
      resetCloudBrowserState();
    } catch {
      toast.error('Failed to create connect session');
    }
  };

  const handleRefreshConnections = async () => {
    try {
      await refetchConnections();
    } catch {
      toast.error('Failed to refresh connections');
    }
  };

  const handleImportCloudFiles = async () => {
    if (!selectedConnectionId || selectedCloudFiles.length === 0) {
      toast.error('Select connection and at least one file');
      return;
    }

    try {
      await importCloudFiles.mutateAsync({
        periodId,
        data: {
          provider_key: selectedProvider,
          connection_id: selectedConnectionId,
          files: selectedCloudFiles
            .filter(file => !file.is_folder)
            .map(file => ({
              external_file_id: file.id,
              name: file.name,
              mime_type: file.mime_type,
              size: file.size,
              drive_id: file.drive_id,
              path: file.path,
              version: file.version
            }))
        }
      });

      toast.success('Cloud files imported successfully');
      closeCloudModal();
      onImported?.();
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to import cloud files'));
    }
  };

  return {
    acceptedMimeTypes,
    addFilesSourceModalProps: {
      onImportFromSource: openCloudModal,
      onUploadFromDevice: () => {
        onClose?.();
        onUploadFromDevice?.();
      }
    },
    closeCloudModal,
    cloudImportModalProps: {
      acceptedMimeTypes,
      cloudPath,
      connections: allConnections,
      files: visibleCloudFiles,
      hasAnyConnections: allConnections.length > 0,
      isCloudFolderNavigationEnabled: true,
      isFetchingCloudFiles,
      isFetchingConnections,
      isImporting: importCloudFiles.isLoading,
      onClose: closeCloudModal,
      onConnectProvider: handleConnectProvider,
      onFileClick: handleCloudItemClick,
      onImport: handleImportCloudFiles,
      onNavigateBack: handleCloudBack,
      onRefreshConnections: handleRefreshConnections,
      onSearchChange: setCloudQuery,
      onSearchSubmit: refetchCloudFiles,
      onSelectConnection: handleSelectConnection,
      onSelectProvider: handleSelectProvider,
      onShowMoreIntegrations: () => navigate('/integrations'),
      opened: isCloudModalOpen,
      previewProviders: providers,
      providers: connectedProviders,
      searchValue: cloudQuery,
      selectedCloudFiles,
      selectedConnectionId,
      selectedProvider,
      selectedProviderConnections,
      title: 'Import files'
    },
    connectedProviders,
    isCloudIntegrationEnabled,
    openCloudModal
  };
}
